'use client';

import { CaretDown, Minus, Plus } from '@phosphor-icons/react/ssr';
import type { Accordion as AccordionPrimitive } from 'radix-ui';
import {
  type ComponentProps,
  createContext,
  type ElementType,
  type ReactNode,
  useContext,
} from 'react';

import { tv } from '../../lib/tv';
import { cn } from '../../lib/utils';
import {
  Accordion as AccordionRoot,
  AccordionContent,
  AccordionItem as AccordionItemPrimitive,
  AccordionTrigger,
} from '../ui/accordion';

/**
 * Figma "Style":
 * - `faq` (default) - the landing page FAQ (Landing Page ↳ LP / FAQ Item):
 *   separate rounded cards with a plus / minus chip.
 * - `bordered` - Accordion Style=Bordered (↳ Accordion): items inside a
 *   bg/surface card (settings, FAQs in content).
 * - `flush` - Accordion Style=Flush: dividers only, for sidebars and inspectors.
 */
export type AccordionVariant = 'faq' | 'bordered' | 'flush';

export type AccordionProps = ComponentProps<typeof AccordionPrimitive.Root> & {
  variant?: AccordionVariant;
};

export type AccordionItemProps = {
  /** Unique value Radix tracks open state by. */
  value: string;
  /** Figma "Title" (FAQ: "Question") - the header row. */
  title: ReactNode;
  /** Figma "Panel text" (FAQ: "Answer") - shown when open. */
  children: ReactNode;
  /** Figma "Description" - a Caption line under the title (bordered / flush). */
  description?: ReactNode;
  /** Figma "Icon" - a 20px icon before the title (bordered / flush). */
  icon?: ElementType<{ className?: string }>;
  /** Figma State=Disabled: the header can't be toggled. */
  disabled?: boolean;
  className?: string;
};

const AccordionVariantContext = createContext<AccordionVariant>('faq');

/**
 * Figma LP / FAQ Item (91:325). 1px border/subtle stroke (in layout), radius/xl,
 * 20px top/bottom · 24px sides, gap space/2 between the question row and the
 * answer. Closed: bg/canvas, 28px bg/muted chip with a 16px plus. Open:
 * bg/surface, brand/primary chip with a minus. Both icons icon/primary.
 */
export const accordionItemStyles = tv({
  slots: {
    item: [
      'group/faq flex flex-col rounded-xl border border-border-subtle bg-bg-canvas px-6 py-5',
      'transition-[background-color,box-shadow] duration-[120ms] ease-out',
      'data-[state=open]:bg-bg-surface',
      'has-[button:focus-visible]:shadow-focus-ring',
    ],
    trigger: 'group/trigger w-full cursor-pointer gap-4',
    question: 'text-body-lg-strong text-text-primary min-w-0 flex-1',
    chip: [
      'flex shrink-0 items-center justify-center rounded-full bg-bg-muted p-1.5',
      'text-icon-primary transition-colors duration-[120ms] ease-out',
      'group-data-[state=open]/faq:bg-brand-primary',
    ],
    icon: 'size-4',
    // Padding (not a flex gap) so the space animates with the height.
    answer: 'text-body-md text-text-secondary pt-2',
  },
});

/**
 * Figma Accordion Item (88:59) and Accordion (88:152), page ↳ Accordion.
 * Item: 1px border/subtle bottom rule (in layout → 53 tall). Header: 16px
 * padding, gap space/3, Hover bg/subtle; Focus a 2px border/focus inside
 * stroke with radius/sm. Titles: Body/Medium Strong text/primary + optional
 * Caption text/tertiary description, 2px apart. 20px icon, 16px caret
 * icon/secondary that turns 180° when expanded (200ms). Panel: 0/16/16/16
 * padding, Body/Medium text/secondary, height eases out over 200ms.
 * Disabled: text/disabled, caret icon/tertiary.
 * Bordered: bg/surface card, 1px border/subtle, radius/lg; the last item has
 * no rule. Flush: every item keeps its rule.
 */
export const accordionStyles = tv({
  slots: {
    root: 'flex flex-col',
    item: 'group/item border-b border-border-subtle',
    trigger: [
      'group/trigger w-full cursor-pointer gap-3 p-4',
      'transition-[background-color,box-shadow] duration-[120ms] ease-out',
      'hover:bg-bg-subtle',
      'focus-visible:rounded-sm focus-visible:inset-ring-2 focus-visible:inset-ring-border-focus',
      'disabled:cursor-default',
    ],
    icon: [
      'size-5 shrink-0 text-icon-secondary',
      'group-disabled/trigger:text-icon-tertiary',
    ],
    titles: 'flex min-w-0 flex-1 flex-col gap-0.5',
    title:
      'text-body-md-strong text-text-primary group-disabled/trigger:text-text-disabled',
    description:
      'text-caption text-text-tertiary group-disabled/trigger:text-text-disabled',
    chevron: [
      'size-4 shrink-0 text-icon-secondary',
      'transition-transform duration-200 ease-out motion-reduce:transition-none',
      'group-data-[state=open]/trigger:rotate-180',
      'group-disabled/trigger:text-icon-tertiary',
    ],
    panel: 'text-body-md text-text-secondary px-4 pb-4',
  },
  variants: {
    variant: {
      bordered: {
        root: 'overflow-hidden rounded-lg border border-border-subtle bg-bg-surface',
        item: 'last:border-b-0',
      },
      flush: {},
    },
  },
  defaultVariants: { variant: 'bordered' },
});

/**
 * A stack of disclosure rows on Radix Accordion: `type="multiple"` (Figma:
 * allow several open unless it's a strict one-at-a-time list) or
 * `type="single" collapsible`. `variant` picks the Figma style; `faq` (the
 * default) keeps the landing page FAQ - separate cards a space/3 gap apart.
 */
export function Accordion({ variant = 'faq', className, ...props }: AccordionProps) {
  return (
    <AccordionVariantContext.Provider value={variant}>
      <AccordionRoot
        data-variant={variant}
        className={
          variant === 'faq'
            ? cn('flex flex-col gap-3', className)
            : accordionStyles({ variant }).root({ className })
        }
        {...props}
      />
    </AccordionVariantContext.Provider>
  );
}

/**
 * One row: the whole header is the button (inside an h3, `aria-expanded`),
 * the panel is its region. Arrow keys move between headers.
 */
export function AccordionItem({
  value,
  title,
  children,
  description,
  icon: Icon,
  disabled,
  className,
}: AccordionItemProps) {
  const variant = useContext(AccordionVariantContext);

  if (variant === 'faq') {
    const styles = accordionItemStyles();
    return (
      <AccordionItemPrimitive
        value={value}
        disabled={disabled}
        className={styles.item({ className })}
      >
        <AccordionTrigger className={styles.trigger()}>
          <span className={styles.question()}>{title}</span>
          <span data-slot="accordion-icon" aria-hidden className={styles.chip()}>
            <Plus className={`${styles.icon()} group-data-[state=open]/trigger:hidden`} />
            <Minus
              className={`${styles.icon()} group-data-[state=closed]/trigger:hidden`}
            />
          </span>
        </AccordionTrigger>
        <AccordionContent className={styles.answer()}>{children}</AccordionContent>
      </AccordionItemPrimitive>
    );
  }

  const styles = accordionStyles({ variant });
  // data-disabled on the text itself: axe skips disabled text contrast.
  const disabledAttr = disabled ? '' : undefined;
  return (
    <AccordionItemPrimitive
      value={value}
      disabled={disabled}
      className={styles.item({ className })}
    >
      <AccordionTrigger className={styles.trigger()}>
        {Icon && (
          <Icon
            aria-hidden
            data-slot="accordion-leading-icon"
            className={styles.icon()}
          />
        )}
        <span className={styles.titles()}>
          <span
            data-slot="accordion-title"
            data-disabled={disabledAttr}
            className={styles.title()}
          >
            {title}
          </span>
          {description && (
            <span
              data-slot="accordion-description"
              data-disabled={disabledAttr}
              className={styles.description()}
            >
              {description}
            </span>
          )}
        </span>
        <CaretDown
          aria-hidden
          data-slot="accordion-chevron"
          className={styles.chevron()}
        />
      </AccordionTrigger>
      <AccordionContent className={styles.panel()}>{children}</AccordionContent>
    </AccordionItemPrimitive>
  );
}
